import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
} from "@tanstack/react-table";
import { User } from "@/types";
import { Alert, Button, Modal, Form } from "react-bootstrap";
import BootstrapInput from "./BootstrapInput";
import { useGeneralApiCall } from "../services/useGeneralApiCall";
import { toast } from "react-toastify";
import RoleModal from "./RoleModal";

import { showToast } from "@/api/notification";
import LoadingComponent from "./Loading";
import ConfirmationModal from "./ConfirmationModal";

type Props = {
  data: User[];
  setData: React.Dispatch<React.SetStateAction<User[]>>;
  getData: () => void;
};

type UserResponse = {
  user_id: string;
  name: string;
  role: string;
  username: string;
  system_generated_password: string;
  force_password_change: boolean;
};

type Role = {
  role_id: string;
  name: string;
  description: string;
  employee_count: number;
};

interface userUpdateResponse {
  user_id: string;
  user_name: string;
  username: string;
  password: string;
}

const UserTable: React.FC<Props> = ({ data, setData, getData }) => {
  const [modalShow, setModalShow] = useState(false);
  const [userCreatedModalShow, setUserCreatedModalShow] = useState(false);
  const [createdUser, setCreatedUser] = useState<UserResponse | null>(null);
  const [userResetPassword, setUserResetPassword] =
    useState<userUpdateResponse | null>(null);

  const [showUserDeleteModal, setShowUserDeleteModal] =
    useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [roleModalShow, setRoleModalShow] = useState(false);

  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);

  const { postApi, deleteApi, loading } = useGeneralApiCall();

  // Add debugging
  console.log("UserTable render - Modal states:", {
    userCreatedModalShow,
    createdUser,
    userResetPassword,
    showUserDeleteModal,
  });

  // Effect to handle modal opening when userResetPassword is set
  useEffect(() => {
    console.log("useEffect triggered - userResetPassword:", userResetPassword);
    if (userResetPassword) {
      console.log("useEffect setting modal to true");
      setUserCreatedModalShow(true);
    } else {
      console.log("useEffect - userResetPassword is null/undefined");
    }
  }, [userResetPassword]);

  const handleSendGmail = useCallback(
    async (user_id: string) => {
      try {
        console.log("Starting password reset for user:", user_id);

        const res: any = await postApi(
          `/api/auth/v1/admin-reset-password/${user_id}/`
        );

        console.log("Password reset response:", res);
        console.log("res.data:", res.data);
        console.log("Type of res.data:", typeof res.data);

        // First clear any existing created user data
        setCreatedUser(null);

        // Set the reset password data (this will trigger useEffect)
        setUserResetPassword(res.data);

        console.log("Called setUserResetPassword with:", res.data);

        // Show toast
        showToast({ message: res.message, type: "success" });

        // Don't call getData() here - let it be called when modal closes

        console.log(
          "userResetPassword data set, useEffect should trigger modal"
        );
      } catch (error: any) {
        console.error("Password reset error:", error);
        showToast({
          message: error.message || "Failed to reset password",
          type: "error",
        });
      }
    },
    [postApi, getData]
  );

  const handleUserDelete = async (userId: string) => {
    try {
      const res = await deleteApi(`api/auth/v1/delete-user/${userId}/`);

      if (res.status === 204) {
        showToast({
          message: "User deleted successfully",
          type: "success",
        });
        getData();
      }
    } catch (error: any) {
      console.error("Error deleting user:", error);
      showToast({
        message: error?.message || "Failed to delete user",
        type: "error",
      });
    }
  };

  const columns = useMemo<ColumnDef<User>[]>(() => {
    return [
      {
        accessorKey: "name",
        header: ({
          column,
        }: {
          column: import("@tanstack/react-table").HeaderContext<
            User,
            unknown
          >["column"];
        }) => (
          <div
            onClick={() => column.toggleSorting()}
            style={{ cursor: "pointer" }}
          >
            Name{" "}
            {column.getIsSorted() === "asc"
              ? "🔼"
              : column.getIsSorted() === "desc"
              ? "🔽"
              : "↕️"}
          </div>
        ),
        cell: (info: { row: { original: User }; getValue: () => unknown }) => {
          const user = info.row.original;
          return (
            <div className="d-flex align-items-center">
              <p>{info.getValue() as string}</p>
              <p className="text-warning">
                {user.force_password_change ? "*" : ""}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "username",
        header: "Username",
        cell: (info: { getValue: () => unknown }) => info.getValue(),
      },
      {
        header: "Actions",
        cell: (info: { row: { original: User } }) => {
          const user = info.row.original;
          // setSelectedUser(user);
          console.log();
          return (
            <div className="action-buttons">
              <Button
                variant="outline-warning"
                size="sm"
                className="!text-black me-2"
                style={{ fontFamily: "sans-serif" }}
                onClick={() => {
                  handleSendGmail(user.user_id);
                }}
              >
                Reset Password
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="me-2 !bg-red-400 hover:!bg-red-700"
                style={{ fontFamily: "sans-serif" }}
                onClick={() => {
                  setSelectedUser(user);
                  setShowUserDeleteModal(true);
                }}
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ];
  }, [handleSendGmail]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {}, // Add sorting/filtering state here if needed
  });

  // useEffect(() => {
  //   getApi("api/auth/v1/roles/").then((res) => {
  //     setRoles(Array.isArray(res?.data) ? res.data : []);
  //   });
  // }, [setRoles]);

  interface MyVerticallyCenteredModalProps {
    show: boolean;
    onHide: () => void;
  }
  function MyVerticallyCenteredModal(props: MyVerticallyCenteredModalProps) {
    const [username, setUsername] = useState<string>("");
    const [name, setName] = useState<string>("");
    // const [role, setRole] = useState<string>("");

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setApiError(null);
      setIsLoading(true);

      try {
        const response = await postApi("api/auth/v1/create-account/", {
          username: username,
          name: name,
        });

        const userData = response.data as UserResponse;
        setCreatedUser(userData);
        setUserCreatedModalShow(true);

        // Map UserResponse to User for setData
        setData((data) =>
          data.concat({
            user_id: userData.user_id,
            name: userData.name,
            role: userData.role,
            email: userData.username, // Map username to email for User type compatibility
            force_password_change: userData.force_password_change,
          })
        );

        props.onHide();
      } catch (error) {
        const err = error as {
          response?: { data?: { errors_data?: { username?: string } } };
        };
        setApiError(
          err?.response?.data?.errors_data?.username ||
            "User with this username already exists, Kindly ask them to Login. Or Reset Password if they forgot."
        );
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered // Added for better centering
        backdrop="static"
        keyboard={false}
        dialogClassName="flex justify-center"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Create New User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Display API Error */}
          {apiError && <Alert variant="danger">{apiError}</Alert>}
          <Form onSubmit={handleSubmit}>
            <BootstrapInput
              value={name}
              setValue={setName}
              placeholder="Enter Name"
              label="Name" // Simplified label
              required // Added basic required validation
            />

            <BootstrapInput
              value={username}
              setValue={setUsername}
              placeholder="Enter Username"
              label="Username"
              required
            />

            <Button type="submit" className="mt-4" disabled={isLoading}>
              {isLoading ? "Submitting..." : "Submit"} {/* Show loading text */}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    );
  }

  interface UserCreatedModalProps {
    show: boolean;
    onHide: () => void;
  }
  function UserCreatedModal(props: UserCreatedModalProps) {
    return (
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="flex justify-center"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Account Created Successfully
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 64 64"
              style={{ width: "10rem", height: "10rem", margin: "0 auto" }}
            >
              <path
                d="M32,2C15.431,2,2,15.432,2,32c0,16.568,13.432,30,30,30c16.568,0,30-13.432,30-30C62,15.432,48.568,2,32,2z M25.025,50  l-0.02-0.02L24.988,50L11,35.6l7.029-7.164l6.977,7.184l21-21.619L53,21.199L25.025,50z"
                fill="#43a047"
              />
            </svg>
            <h1>
              {userResetPassword
                ? "Password Reset Successfully"
                : "Account Created Successfully"}
            </h1>
            <div className="text-left d-flex gap-4 mt-3 w-max mx-auto">
              <div>
                <p>
                  <strong>Name:</strong>{" "}
                  {createdUser?.name || userResetPassword?.user_name}
                </p>
              </div>
              <div>
                <p>
                  <strong>Username:</strong>{" "}
                  {createdUser?.username || userResetPassword?.username}
                </p>
                <p>
                  <strong>Password:</strong>{" "}
                  {createdUser?.system_generated_password ||
                    userResetPassword?.password}
                </p>
              </div>
            </div>

            <button
              className="btn btn-primary mt-3 d-flex mx-auto align-items-center"
              onClick={() => {
                const username =
                  createdUser?.username ||
                  userResetPassword?.username ||
                  "jhon_doe";
                const password =
                  createdUser?.system_generated_password ||
                  userResetPassword?.password ||
                  "j32j9>67b87";

                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard
                    .writeText(`Username: ${username}\nPassword: ${password}`)
                    .then(() => {
                      toast.success("Copied to clipboard!");
                    })
                    .catch(() => {
                      toast.error("Failed to copy to clipboard.");
                    });
                } else {
                  toast.error(
                    "Clipboard API is not supported in this browser."
                  );
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16px"
                height="16px"
                fill="#fff"
              >
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                <path d="M0 0h24v24H0z" fill="none" />
              </svg>
              <span className="ms-2">Copy to Clipboard</span>
            </button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setApiError(null);
        }}
      />
      <UserCreatedModal
        show={userCreatedModalShow}
        onHide={() => {
          setUserCreatedModalShow(false);
          setCreatedUser(null);
          setUserResetPassword(null); // Clear reset password data
          getData(); // Refresh data only when modal is closed
        }}
      />
      <RoleModal
        roles={roles}
        setRoles={setRoles}
        show={roleModalShow}
        onHide={() => setRoleModalShow(false)}
        setRoleModalShow={setRoleModalShow}
      />
      <ConfirmationModal
        show={showUserDeleteModal}
        onHide={() => {
          setShowUserDeleteModal(false);
          setSelectedUser(null);
          getData();
        }}
        id={selectedUser?.user_id || ""}
        name={selectedUser?.name || ""}
        onDelete={handleUserDelete}
      />
      {/* <ChnageDetails
        show={changeDetailsModal}
        onHide={() => setChangeDetailsModal(false)}
        user={selectedUser}
        roles={roles}
        getData={getData}
      /> */}
      <div className="d-flex justify-content-end mb-2">
        {" "}
        {/* Adjusted alignment */}
        <div className="d-flex gap-2">
          <Button variant="primary" onClick={() => setModalShow(true)}>
            CREATE USER
          </Button>

          {/* <Button variant="primary" onClick={() => setRoleModalShow(true)}>
            MANAGE ROLES
          </Button> */}
        </div>
      </div>
      <Alert variant="info" className="text-center">
        USER TABLE INFORMATION
      </Alert>{" "}
      {/* Changed variant */}
      <div className="p-2 table-responsive">
        {loading ? (
          <LoadingComponent />
        ) : (
          <table
            className="styled-table table table-striped table-hover"
            style={{ fontFamily: "Yotter" }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        width:
                          header.getSize() !== 150
                            ? header.getSize()
                            : undefined,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        width:
                          cell.column.getSize() !== 150
                            ? cell.column.getSize()
                            : undefined,
                      }}
                    >
                      {" "}
                      {/* Optional: Set width */}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Optional: Add Pagination Controls here */}
      </div>
    </>
  );
};

export default UserTable;
